import { useState, useEffect } from 'react';
import { portal } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Home, Briefcase, TrendingUp, Users, DollarSign, Award, Receipt, Wallet, Settings2 } from 'lucide-react';

// Default widget visibility
const DEFAULT_WIDGETS = {
  ytdSummary: true,
  divisionBreakdown: true,
  referralStats: true,
  monthlyBreakdown: true,
};

export default function PortalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('portalDashboardWidgets');
    return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
  });
  
  // Year selector state
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadDashboard();
  }, [selectedYear]); // Reload when year changes

  // Save widget settings to localStorage
  useEffect(() => {
    localStorage.setItem('portalDashboardWidgets', JSON.stringify(widgets));
  }, [widgets]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await portal.dashboard(selectedYear);
      setData(response.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = (key) => {
    setWidgets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    toast.success('Dashboard reset to default');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Format month name
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="portal-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Utmost Financial Inc.
            </h1>
            <p className="text-slate-500">Combined Business Performance Dashboard - YTD {selectedYear}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Year:</span>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28" data-testid="portal-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings2 className="w-4 h-4 mr-2" />
            Customize Dashboard
          </Button>
        </div>
      </div>

      {/* Dashboard Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customize Dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">Toggle which sections to display on your dashboard.</p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="ytdSummary">YTD Summary Cards</Label>
                <Switch 
                  id="ytdSummary"
                  checked={widgets.ytdSummary}
                  onCheckedChange={() => toggleWidget('ytdSummary')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="divisionBreakdown">Division Breakdown</Label>
                <Switch 
                  id="divisionBreakdown"
                  checked={widgets.divisionBreakdown}
                  onCheckedChange={() => toggleWidget('divisionBreakdown')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="referralStats">Referral Statistics</Label>
                <Switch 
                  id="referralStats"
                  checked={widgets.referralStats}
                  onCheckedChange={() => toggleWidget('referralStats')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="monthlyBreakdown">Monthly Breakdown Table</Label>
                <Switch 
                  id="monthlyBreakdown"
                  checked={widgets.monthlyBreakdown}
                  onCheckedChange={() => toggleWidget('monthlyBreakdown')}
                />
              </div>
            </div>
            
            <Button variant="outline" onClick={resetWidgets} className="w-full mt-4">
              Reset to Default
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* YTD Summary Cards */}
      {widgets.ytdSummary && (
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Year-to-Date Summary ({selectedYear})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Total Commissions */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-300 uppercase tracking-wider">Total Commissions</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {formatCurrency(data?.combined?.total_commissions || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Agent Earnings */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-emerald-600 to-emerald-700">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-emerald-100 uppercase tracking-wider">Agent Earnings</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {formatCurrency(data?.combined?.agent_earnings || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Brokerage Earnings */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-blue-600 to-blue-700">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-100 uppercase tracking-wider">Brokerage Earnings</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {formatCurrency(data?.combined?.brokerage_earnings || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* YTD Expenses */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-red-500 to-red-600">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-red-100 uppercase tracking-wider">YTD Expenses</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {formatCurrency(data?.combined?.total_expenses || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Net Brokerage (After Expenses) */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-amber-500 to-amber-600">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-amber-100 uppercase tracking-wider">Net Brokerage</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {formatCurrency(data?.combined?.net_brokerage || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Agents */}
          <Card className="border-slate-100 shadow-sm bg-gradient-to-br from-violet-600 to-violet-700">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-violet-100 uppercase tracking-wider">Total Agents</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {data?.combined?.total_agents || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {/* Division Breakdown - YTD */}
      {widgets.divisionBreakdown && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mortgage Division */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Home className="w-4 h-4 text-emerald-600" />
              </div>
              Mortgage Division (YTD {selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Commissions</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {formatCurrency(data?.mortgage?.total_commissions || 0)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Transactions</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {data?.mortgage?.transaction_count || 0}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                  <p className="text-xs text-emerald-700 uppercase tracking-wider mb-1">Agent Earnings</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">
                    {formatCurrency(data?.mortgage?.agent_earnings || 0)}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Brokerage Earnings</p>
                  <p className="text-xl font-bold font-mono text-white">
                    {formatCurrency(data?.mortgage?.brokerage_earnings || 0)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <p className="text-xs text-red-700 uppercase tracking-wider mb-1">Royalty + GST</p>
                  <p className="text-xl font-bold font-mono text-red-700">
                    {formatCurrency(data?.mortgage?.royalty_gst || 0)}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <p className="text-xs text-red-700 uppercase tracking-wider mb-1">Other Expenses</p>
                  <p className="text-xl font-bold font-mono text-red-700">
                    {formatCurrency(data?.mortgage?.other_expenses || 0)}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                  <p className="text-xs text-amber-700 uppercase tracking-wider mb-1">Net Brokerage</p>
                  <p className="text-xl font-bold font-mono text-amber-700">
                    {formatCurrency(data?.mortgage?.net_brokerage || 0)}
                  </p>
                </div>
              </div>
              <div className="text-sm text-slate-500">
                <span className="font-medium">{data?.mortgage_agents || 0}</span> active agents
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insurance Division */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-blue-600" />
              </div>
              Insurance & Investment Division (YTD {selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Commissions</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {formatCurrency(data?.insurance?.total_commissions || 0)}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Transactions</p>
                  <p className="text-xl font-bold font-mono text-slate-900">
                    {data?.insurance?.transaction_count || 0}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-xs text-blue-700 uppercase tracking-wider mb-1">Agent Earnings</p>
                  <p className="text-xl font-bold font-mono text-blue-700">
                    {formatCurrency(data?.insurance?.agent_earnings || 0)}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Brokerage Earnings</p>
                  <p className="text-xl font-bold font-mono text-white">
                    {formatCurrency(data?.insurance?.brokerage_earnings || 0)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <p className="text-xs text-red-700 uppercase tracking-wider mb-1">YTD Expenses</p>
                  <p className="text-xl font-bold font-mono text-red-700">
                    {formatCurrency(data?.insurance?.total_expenses || 0)}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                  <p className="text-xs text-amber-700 uppercase tracking-wider mb-1">Net Brokerage</p>
                  <p className="text-xl font-bold font-mono text-amber-700">
                    {formatCurrency(data?.insurance?.net_brokerage || 0)}
                  </p>
                </div>
              </div>
              <div className="text-sm text-slate-500">
                <span className="font-medium">{data?.insurance_agents || 0}</span> active agents
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Monthly Breakdown Table */}
      {widgets.monthlyBreakdown && (
      <Card className="border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Monthly Earnings Breakdown ({selectedYear})</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.monthly_data?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Month</TableHead>
                    <TableHead colSpan={2} className="text-center bg-emerald-50 font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <Home className="w-4 h-4" /> Mortgage
                      </div>
                    </TableHead>
                    <TableHead colSpan={2} className="text-center bg-blue-50 font-semibold">
                      <div className="flex items-center justify-center gap-2">
                        <Briefcase className="w-4 h-4" /> Insurance
                      </div>
                    </TableHead>
                    <TableHead colSpan={2} className="text-center bg-slate-100 font-semibold">Combined Total</TableHead>
                  </TableRow>
                  <TableRow className="bg-slate-50/50">
                    <TableHead></TableHead>
                    <TableHead className="text-right text-xs bg-emerald-50/50">Agent</TableHead>
                    <TableHead className="text-right text-xs bg-emerald-50/50">Brokerage</TableHead>
                    <TableHead className="text-right text-xs bg-blue-50/50">Agent</TableHead>
                    <TableHead className="text-right text-xs bg-blue-50/50">Brokerage</TableHead>
                    <TableHead className="text-right text-xs">Agent</TableHead>
                    <TableHead className="text-right text-xs">Brokerage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.monthly_data.map((row, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{formatMonth(row.month)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">
                        {formatCurrency(row.mortgage_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700">
                        {formatCurrency(row.mortgage_brokerage_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-600">
                        {formatCurrency(row.insurance_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700">
                        {formatCurrency(row.insurance_brokerage_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700 bg-slate-50">
                        {formatCurrency(row.total_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-slate-900 bg-slate-50">
                        {formatCurrency(row.total_brokerage_earnings)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-slate-900 text-white font-semibold">
                    <TableCell className="text-white">YTD Total</TableCell>
                    <TableCell className="text-right font-mono text-emerald-300">
                      {formatCurrency(data?.mortgage?.agent_earnings || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-300">
                      {formatCurrency(data?.mortgage?.brokerage_earnings || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-blue-300">
                      {formatCurrency(data?.insurance?.agent_earnings || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-300">
                      {formatCurrency(data?.insurance?.brokerage_earnings || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-300">
                      {formatCurrency(data?.combined?.agent_earnings || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-white">
                      {formatCurrency(data?.combined?.brokerage_earnings || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No data available for this year
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Year-over-Year Comparison */}
      {widgets.monthlyBreakdown && data?.yearly_data?.length > 0 && (
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Year-over-Year Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Year</TableHead>
                    <TableHead colSpan={2} className="text-center bg-emerald-50 font-semibold">Mortgage</TableHead>
                    <TableHead colSpan={2} className="text-center bg-blue-50 font-semibold">Insurance</TableHead>
                    <TableHead colSpan={2} className="text-center bg-slate-100 font-semibold">Combined Total</TableHead>
                  </TableRow>
                  <TableRow className="bg-slate-50/50">
                    <TableHead></TableHead>
                    <TableHead className="text-right text-xs bg-emerald-50/50">Agent</TableHead>
                    <TableHead className="text-right text-xs bg-emerald-50/50">Brokerage</TableHead>
                    <TableHead className="text-right text-xs bg-blue-50/50">Agent</TableHead>
                    <TableHead className="text-right text-xs bg-blue-50/50">Brokerage</TableHead>
                    <TableHead className="text-right text-xs">Agent</TableHead>
                    <TableHead className="text-right text-xs">Brokerage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.yearly_data.map((row, idx) => (
                    <TableRow 
                      key={idx} 
                      className={`hover:bg-slate-50 ${String(row.year) === String(selectedYear) ? 'bg-emerald-50' : ''}`}
                    >
                      <TableCell className="font-semibold">
                        {row.year}
                        {String(row.year) === String(selectedYear) && (
                          <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Selected</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">
                        {formatCurrency(row.mortgage_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700">
                        {formatCurrency(row.mortgage_brokerage_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-600">
                        {formatCurrency(row.insurance_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700">
                        {formatCurrency(row.insurance_brokerage_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700 bg-slate-50">
                        {formatCurrency(row.total_agent_earnings)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-slate-900 bg-slate-50">
                        {formatCurrency(row.total_brokerage_earnings)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
